import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  Mail,
  Plus,
  Trash2,
  Eye,
  GripVertical
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminContact() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [settings, setSettings] = useState({
    recipient_email: 'contact.medstudypro@gmail.com',
    form_title: 'Contact Us',
    form_intro: 'Have a question or feedback? We\'d love to hear from you.',
    subject_options: ['Bug Report', 'Content Feedback', 'Account/Subscription', 'General Question'],
    enable_file_upload: true,
    max_file_size_mb: 10,
    allowed_file_types: ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'],
    max_files: 3,
    success_message: 'Thanks — we received your message and will respond as soon as possible.'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/contact/settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (err) {
      toast.error('Failed to load Contact page settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate email
    if (!settings.recipient_email || !settings.recipient_email.includes('@')) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/contact/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save');
      }
      
      toast.success('Contact page settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const addSubjectOption = () => {
    setSettings(prev => ({
      ...prev,
      subject_options: [...prev.subject_options, '']
    }));
  };

  const updateSubjectOption = (index, value) => {
    setSettings(prev => {
      const options = [...prev.subject_options];
      options[index] = value;
      return { ...prev, subject_options: options };
    });
  };

  const removeSubjectOption = (index) => {
    setSettings(prev => ({
      ...prev,
      subject_options: prev.subject_options.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-600 hover:text-slate-900"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-teal-600" />
                <h1 className="text-xl font-bold text-slate-900">Edit Contact Page</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => window.open('/contact', '_blank')}
                className="border-slate-200"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="save-contact-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Email Settings */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Email Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Recipient Email Address</Label>
              <Input
                type="email"
                value={settings.recipient_email || ''}
                onChange={(e) => updateField('recipient_email', e.target.value)}
                placeholder="contact@example.com"
                className="mt-1 bg-white border-slate-200"
                data-testid="recipient-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                All contact form submissions will be sent to this email address.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Form Display */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Form Display</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-700">Form Title</Label>
              <Input
                value={settings.form_title || ''}
                onChange={(e) => updateField('form_title', e.target.value)}
                placeholder="Contact Us"
                className="mt-1 bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">Intro Text</Label>
              <Textarea
                value={settings.form_intro || ''}
                onChange={(e) => updateField('form_intro', e.target.value)}
                placeholder="Have a question or feedback? We'd love to hear from you."
                className="mt-1 min-h-[60px] bg-white border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-700">Success Message</Label>
              <Textarea
                value={settings.success_message || ''}
                onChange={(e) => updateField('success_message', e.target.value)}
                placeholder="Thanks — we received your message..."
                className="mt-1 min-h-[60px] bg-white border-slate-200"
              />
            </div>
          </CardContent>
        </Card>

        {/* Subject Options */}
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg text-slate-900">Subject Dropdown Options</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={addSubjectOption}
              className="border-slate-200"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Option
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {(settings.subject_options || []).map((option, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-300" />
                <Input
                  value={option}
                  onChange={(e) => updateSubjectOption(idx, e.target.value)}
                  placeholder="Enter subject option..."
                  className="bg-white border-slate-200"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSubjectOption(idx)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  disabled={settings.subject_options.length <= 1}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* File Upload Settings */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">File Upload Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-700">Enable File Uploads</Label>
                <p className="text-xs text-slate-500">Allow users to attach files to their message</p>
              </div>
              <Switch
                checked={settings.enable_file_upload}
                onCheckedChange={(checked) => updateField('enable_file_upload', checked)}
              />
            </div>
            
            {settings.enable_file_upload && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700">Max File Size (MB)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="50"
                      value={settings.max_file_size_mb || 10}
                      onChange={(e) => updateField('max_file_size_mb', parseInt(e.target.value) || 10)}
                      className="mt-1 bg-white border-slate-200"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700">Max Files</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={settings.max_files || 3}
                      onChange={(e) => updateField('max_files', parseInt(e.target.value) || 3)}
                      className="mt-1 bg-white border-slate-200"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700">Allowed File Types</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Currently allows: PNG, JPG, PDF
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Save Button (bottom) */}
        <div className="flex justify-end pb-8">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
